import React, { useState, useEffect } from "react";
import Form from "react-bootstrap/Form";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Col } from "react-bootstrap";

import { allSharedComponents } from "../../../shared";
import { LockIcon, BreakdownIcon } from "../../../assets/images/icon";
import { paymentActions, centersActions } from "../../../redux/actions";
import {
  roles,
  spotConstants,
  paymentType,
  CenterStatusEnum,
  viewPathConstants,
  CenterSpotStatusEnum,
  translationConstants,
  paymentCardRadioButtonConstants,
  Directions,
  paymentFormDivIds,
  calculationOperations,
} from "../../../constants";
import {
  getSportObjByName,
  renderPaymentform,
  hyperpayFormCustomJS,
  unloadWidget,
  dateToUTCWithTimezone,
  serverFormatToMomentObject,
  hyperpaySaveCardFormCustomJS,
  calculateVAT,
  precisionCalculation,
} from "../../../system";

const CenterPaymentForm = () => {
  const { CustomModal, PricingModal } = allSharedComponents;
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [pricingData, setPricingData] = useState(null);
  const [pricingModal, setPricingModal] = useState(null);
  const [cardUsageState, setCardUsageState] = useState(
    paymentCardRadioButtonConstants.NEWCARD
  );
  const [isLoadCustomJS, setLoadCustomJS] = useState(false);
  const [centerDataFetched, setCenterDataFetched] = useState(false);

  const userData = useSelector((state) => state?.LoginReducer?.UserData);
  const checkoutId = useSelector((state) => state.PaymentReducer.CheckoutID);
  const subscriptionPrices = useSelector(
    (state) => state.SubscriptionReducer.subscriptionPrices
  );
  const direction = useSelector(
    (state) => state.LanguageChangeReducer.direction
  );
  const centerProfileData = useSelector(
    (state) => state.centersReducer.centerProfileData
  );
  const checkoutIdSavedCard = useSelector(
    (state) => state.PaymentReducer.checkoutIdSavedCard
  );
  const savedcards = useSelector(
    (state) => state.PaymentReducer.savedPaymentCards
  );

  const getPriceBySportId = (id, centerPricing) =>
    centerPricing.find((x) => x.subcriptionType === id)?.price;

  const calculateLocationsPricing = (spots) => {
    // Initialize variables
    let pricing = [];
    let subTotal = 0;
    let centerPricing = subscriptionPrices.filter(
      (subs) => subs.userType === roles.Center
    );

    let approvedSpots = [];

    if (
      centerProfileData?.centerStatus === CenterStatusEnum.PaymentRejected ||
      centerProfileData?.centerStatus === CenterStatusEnum.Restricted
    ) {
      approvedSpots = spots.filter(
        (x) =>
          x.status === CenterSpotStatusEnum.PaymentPending ||
          x.status === CenterSpotStatusEnum.PaymentRejected
      );
    } else {
      approvedSpots = spots.filter(
        (x) => x.status === CenterSpotStatusEnum.PaymentPending
      );
    }

    // Iterate through each spot
    approvedSpots.forEach((spot) => {
      let sports = [];
      if (
        spot.spotTypeId === spotConstants.spotType.PUBLIC ||
        spot.spotTypeId === spotConstants.spotType.PRIVATE
      ) {
        sports = Array.isArray(spot.sportIds) ? spot.sportIds : [spot.sportIds];

        pricing.push({
          name: spot.spotName,
          sports:
            sports.map((sport) => {
              let price = getPriceBySportId(+sport, centerPricing);
              subTotal += price;
              return {
                name: getSportObjByName(sport)?.label,
                price: price,
              };
            }) || [],
        });
      }
    });

    // Calculate VAT and total fees
    const vat = subscriptionPrices.find((subs) => subs.userType === null);

    const vatValue = calculateVAT(vat?.price, subTotal);

    // Set pricing data state
    setPricingData({
      vat: vat?.price,
      vatValue: vatValue,
      subTotal: subTotal || 0,
      locations: pricing || [],
      totalFees:
        precisionCalculation(
          vatValue,
          subTotal,
          calculationOperations.addition
        ) || 0,
    });
  };

  const nextBillingDueDate = () => {
    let currentDate = dateToUTCWithTimezone();
    if (userData?.centerStatus === CenterStatusEnum.Restricted) {
      currentDate?.subtract(60, "days").add(1, "year");
      return currentDate;
    } else if (userData?.centerStatus === CenterStatusEnum.PaymentRejected) {
      const nextBillingDueDate = serverFormatToMomentObject(
        userData?.centerNextPaymentDueDateString
      )?.add(1, "year");
      return nextBillingDueDate;
    } else {
      currentDate?.add(1, "year");
      return currentDate;
    }
  };

  const renderForm = (id, formDivId, cardType) => {
    renderPaymentform(
      id,
      viewPathConstants.CENTER_PROFILE,
      "",
      formDivId,
      setLoadCustomJS,
      cardType
    );
  };

  const resetForm = () => {
    setLoadCustomJS("");
    dispatch(paymentActions.paymentCheckoutNull());
    unloadWidget();
  };

  useEffect(() => {
    if (isLoadCustomJS === paymentCardRadioButtonConstants.NEWCARD) {
      hyperpayFormCustomJS(
        t,
        direction,
        true,
        false,
        false,
        nextBillingDueDate
      );
    } else if (isLoadCustomJS === paymentCardRadioButtonConstants.SAVEDCARDS) {
      hyperpaySaveCardFormCustomJS(t, direction);
    }
    //eslint-disable-next-line
  }, [isLoadCustomJS]);

  useEffect(() => {
    if (pricingData?.totalFees !== null) {
      if (
        cardUsageState === paymentCardRadioButtonConstants.NEWCARD &&
        checkoutId !== null
      ) {
        renderForm(
          checkoutId,
          paymentFormDivIds.newCardForm,
          paymentCardRadioButtonConstants.NEWCARD
        );
      } else if (
        cardUsageState === paymentCardRadioButtonConstants.SAVEDCARDS &&
        checkoutIdSavedCard !== null
      ) {
        renderForm(
          checkoutIdSavedCard,
          paymentFormDivIds.savedCardForm,
          paymentCardRadioButtonConstants.SAVEDCARDS
        );
      }
    }
    //eslint-disable-next-line
  }, [cardUsageState, checkoutId, checkoutIdSavedCard]);

  useEffect(() => {
    if (pricingData?.totalFees && centerDataFetched) {
      const commonPayload = {
        price: pricingData?.totalFees?.toFixed(2),
        centerId: centerProfileData.id,
        paymentType: paymentType.Center,
      };

      if (
        cardUsageState === paymentCardRadioButtonConstants.NEWCARD &&
        checkoutId === null
      ) {
        dispatch(
          paymentActions.paymentCheckout({
            ...commonPayload,
            createRegistration: true,
          })
        );
      } else if (
        cardUsageState === paymentCardRadioButtonConstants.SAVEDCARDS &&
        checkoutIdSavedCard === null
      ) {
        dispatch(paymentActions.paymentCheckoutSavedCard(commonPayload));
      }
    }
    //eslint-disable-next-line
  }, [pricingData?.totalFees, centerDataFetched, cardUsageState]);

  useEffect(() => {
    if (
      centerProfileData?.centerSpots &&
      subscriptionPrices.length > 0 &&
      centerDataFetched
    ) {
      calculateLocationsPricing(centerProfileData?.centerSpots);
    }
    //eslint-disable-next-line
  }, [centerProfileData, centerDataFetched, subscriptionPrices]);

  useEffect(() => {
    dispatch(
      centersActions.getCenterById(userData?.user?.centerId, () => {
        setCenterDataFetched(true);
      })
    );
    dispatch(
      paymentActions.getSavedPaymentCard({
        centerId: userData?.user?.centerId,
      })
    );
    //eslint-disable-next-line
  }, []);

  return (
    <div className="px-4 w-100">
      <div className="d-flex justify-content-center loginPara">
        {t(translationConstants.CENTER_PAYMENT)}
      </div>

      <div className="row mx-0 my-2">
        <Col md={6} className="p-0">
          <p className="text-dark font-weight-700">
            <span className="font-weight-700">
              {`${t(translationConstants.TOTAL_FEE)}: ${t(
                translationConstants.YEARLY_WITH_BRACKETS
              )}`}
            </span>
          </p>
        </Col>
        <Col
          md={6}
          className={`p-0 d-flex align-items-center justify-content-end`}
        >
          <div
            className={`d-flex align-items-center justify-content-end gap-2 fw-bold text-end text-dark`}
          >
            <div
              className={`d-flex flex-column ${
                direction === Directions.rtl ? "text-start" : "text-end"
              }`}
            >
              {translationConstants.SAR} {pricingData?.totalFees.toFixed(2)}
              <span className="font-10">
                {t(translationConstants.VAT_INCLUDED)}
              </span>
            </div>
            <span className="d-flex align-items-center info-icon">
              <img
                alt="img-info"
                src={BreakdownIcon}
                onClick={() => setPricingModal((prev) => !prev)}
              />
            </span>
          </div>
        </Col>
      </div>

      <div className="row mx-0 my-2">
        <Col md={6} className="p-0">
          <p className="text-dark font-weight-700">
            <span className="font-weight-700">
              {t(translationConstants.PAYMENT_METHOD)}:
            </span>
          </p>
        </Col>
        <Col
          md={6}
          className="d-flex align-items-center justify-content-end p-0"
        >
          <div className="d-flex gap-1 align-items-center">
            <img
              className="secured-by-hyperpay-img"
              src={LockIcon}
              alt="secure lock icon"
            />
            <span className="secured-by-hyperpay-text">
              {t(translationConstants.SECURED_BY_HYPERPAY)}
            </span>
          </div>
        </Col>
      </div>

      {savedcards?.count > 0 && (
        <div className="d-flex gap-3">
          <Form.Check
            type="radio"
            label={t(translationConstants.USE_SAVED_CARD)}
            name={`radioGroup`}
            id={"savedCard"}
            value={paymentCardRadioButtonConstants.SAVEDCARDS}
            onChange={(e) => {
              resetForm();
              setCardUsageState(e.currentTarget?.value);
            }}
          />
          <Form.Check
            type="radio"
            label={t(translationConstants.USE_NEW_CARD)}
            name={`radioGroup`}
            id={"newCard"}
            value={paymentCardRadioButtonConstants.NEWCARD}
            defaultChecked={true}
            onChange={(e) => {
              resetForm();
              setCardUsageState(e.currentTarget?.value);
            }}
          />
        </div>
      )}

      {cardUsageState === paymentCardRadioButtonConstants.NEWCARD &&
        pricingData?.totalFees !== null &&
        checkoutId && (
          <div id={paymentFormDivIds.newCardForm} className="pt-2"></div>
        )}

      {cardUsageState === paymentCardRadioButtonConstants.SAVEDCARDS &&
        pricingData?.totalFee !== null &&
        checkoutIdSavedCard && (
          <div id={paymentFormDivIds.savedCardForm} className="pt-4"></div>
        )}

      {pricingData && (
        <CustomModal
          size={"sm"}
          scrollable
          show={pricingModal}
          setShow={setPricingModal}
          closeBtnClasses={"login-modal-close-btn"}
          customClass={"freelance-instructor-model-sm"}
          closeOnOutsideClick={true}
        >
          <PricingModal data={pricingData} isCenterSubscription={true} />
        </CustomModal>
      )}
    </div>
  );
};

export default CenterPaymentForm;
